import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, MapPinIcon, CreditCardIcon, CheckCircleIcon, WalletIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { auth } from '../../../firebase';
import { useDriver } from '../../../contexts/DriverContext';
import InvoiceDataModal from './InvoiceDataModal';

const ParkingModal = ({ isOpen, onClose, selectedCar }) => {
  const { t } = useTranslation();
  const { driverAccount, hasInvoiceData, refreshDriverAccount } = useDriver();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // Store original body styles and scroll position
  const bodyStyles = useRef({});

  const [plateNumber, setPlateNumber] = useState('');
  const [zone, setZone] = useState('');
  const [selectedZoneData, setSelectedZoneData] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('balance');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [saveForFutureUse, setSaveForFutureUse] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationCity, setLocationCity] = useState('');
  const [locationStreet, setLocationStreet] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [useManualCity, setUseManualCity] = useState(false);

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');

      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';

      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');

      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';

      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }

      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');

      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';

      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }

      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);

  // API-based cities and zones
  const [availableCities, setAvailableCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [parkingZones, setParkingZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);

  // Set plate number when car is selected
  useEffect(() => {
    if (selectedCar) {
      setPlateNumber(selectedCar.plate);
    }
  }, [selectedCar]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Get user location on component mount
  useEffect(() => {
    if (isOpen) {
      getUserLocation();
      fetchCities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch zones when city is selected
  useEffect(() => {
    if (selectedCity) {
      fetchZonesByCity(selectedCity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  // Auto-select city when cities are loaded and location is detected
  useEffect(() => {
    if (locationCity && availableCities.length > 0 && !selectedCity && !useManualCity) {
      const matchingCity = availableCities.find(c =>
        c.name.toLowerCase() === locationCity.toLowerCase()
      );
      if (matchingCity) {
        setSelectedCity(matchingCity.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationCity, availableCities]);

  // Fetch payment methods when payment method changes to card
  useEffect(() => {
    if (paymentMethod === 'card') {
      fetchPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  // Fetch available cities
  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await fetch('https://api.servicegest.ro/public/parking/cities');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        const cities = data.map(cityName => ({
          id: cityName,
          name: cityName
        }));
        setAvailableCities(cities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch zones for selected city
  const fetchZonesByCity = async (cityId) => {
    setLoadingZones(true);

    try {
      const response = await fetch(`https://api.servicegest.ro/public/parking/cities/${cityId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        setParkingZones(data);
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      setParkingZones([]);
    } finally {
      setLoadingZones(false);
    }
  };

  // Fetch saved payment methods
  const fetchPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const response = await fetch('https://api.servicegest.ro/driver/payment/methods', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        setPaymentMethods(data);
        // Auto-select first payment method if available
        if (data.length > 0) {
          setSelectedPaymentMethodId(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by your browser');
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'ro'
              }
            }
          );
          const data = await response.json();

          const city = data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.municipality ||
            'Unknown';

          const street = data.address?.road ||
            data.address?.street ||
            data.address?.pedestrian ||
            '';

          setLocationCity(city);
          setLocationStreet(street);
        } catch (error) {
          console.error('Error getting location details:', error);
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Calculate total price based on zone and payment method
  const calculatePrice = () => {
    if (!selectedDuration || parkingZones.length === 0) return 0;
    const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
    if (!selectedZone) return 0;

    // Use userPriceWithAccount for balance payment, userPrice for card payment
    const priceField = paymentMethod === 'balance' ? 'userPriceWithAccount' : 'userPrice';
    const price = selectedZone[priceField];

    if (typeof price === 'number') {
      return price;
    }
    // If string, parse it (handles "10.7" or "10,7")
    const priceStr = String(price).replace(',', '.');
    return parseFloat(priceStr) || 0;
  };

  // Get unique zone names
  const getUniqueZones = () => {
    const uniqueZones = [];
    const seenZones = new Set();

    parkingZones.forEach(zone => {
      if (!seenZones.has(zone.zoneName)) {
        seenZones.add(zone.zoneName);
        uniqueZones.push(zone);
      }
    });

    return uniqueZones;
  };

  // Get durations for selected zone
  const getDurationsForZone = (zoneName) => {
    return parkingZones.filter(z => z.zoneName === zoneName);
  };

  // Calculate processing fees for card payments
  const calculateProcessingFee = () => {
    if (!selectedDuration || parkingZones.length === 0 || paymentMethod !== 'card') return 0;

    const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
    if (!selectedZone) return 0;

    const userPrice = parseFloat(String(selectedZone.userPrice).replace(',', '.')) || 0;
    const userPriceWithAccount = parseFloat(String(selectedZone.userPriceWithAccount).replace(',', '.')) || 0;

    return userPrice - userPriceWithAccount;
  };

  // Update zone data when duration changes
  useEffect(() => {
    if (selectedDuration && parkingZones.length > 0) {
      const zoneData = parkingZones.find(z => z.zoneId === selectedDuration);
      if (zoneData) {
        setSelectedZoneData(zoneData);
      }
    }
  }, [selectedDuration, parkingZones]);

  // Handle payment submission
  const handlePayment = async (e) => {
    e.preventDefault();

    // Check if invoice data is complete before processing payment
    if (!hasInvoiceData) {
      setShowInvoiceModal(true);
      return;
    }

    if (!plateNumber || !zone || !selectedDuration) {
      alert(t('parkingPayment.completeFields'));
      return;
    }

    if (!selectedCar) {
      alert(t('parkingPayment.selectVehicle'));
      return;
    }

    // Check balance if paying with balance
    if (paymentMethod === 'balance') {
      const totalPrice = calculatePrice();
      if (!driverAccount || totalPrice > (driverAccount.balance || 0)) {
        alert('Sold insuficient pentru această tranzacție. Vă rugăm să alegeți o altă metodă de plată sau să vă reîncărcați contul.');
        return;
      }
    }

    // Check payment method if paying with card
    if (paymentMethod === 'card' && paymentMethods.length > 0 && !selectedPaymentMethodId) {
      alert('Vă rugăm să selectați o metodă de plată sau să adăugați un card nou.');
      return;
    }

    setIsProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();

      const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
      if (!selectedZone) {
        throw new Error('Zone not found');
      }

      // Prepare request payload
      const payload = {
        zoneId: selectedDuration, // This is the UUID zoneId
        carId: selectedCar.carId, // Use carId from API response
        payWithBalance: paymentMethod === 'balance',
        savePaymentMethod: saveForFutureUse,
      };

      // Add payment method ID if paying with card and a method is selected
      if (paymentMethod === 'card' && selectedPaymentMethodId) {
        payload.paymentMethodId = selectedPaymentMethodId;
      }

      // Make payment request
      const response = await fetch(
        'https://api.servicegest.ro/driver/parking/payment',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        },
      );
      console.log('Payment payload:', payload);

      // Handle response based on status code
      if (response.status === 202) {
        // Success - Off-session payment completed
        setShowSuccess(true);

        // Refresh driver account balance
        if (refreshDriverAccount) {
          await refreshDriverAccount();
        }

        // Reset form and close modal after showing success
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 2000);
      } else if (response.status === 200) {
        // Fallback - Redirect to Stripe Checkout for authentication
        const data = await response.json();
        if (data && data.url) {
          // Redirect to Stripe Checkout
          window.location.href = data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        // Handle error responses
        const message = await response.text();

        if (response.status === 400) {
          alert(`Eroare validare: ${message}`);
        } else if (response.status === 404) {
          alert('Contul de șofer nu a fost găsit. Vă rugăm să verificați configurarea contului.');
        } else if (response.status === 500) {
          alert('Eroare de server. Vă rugăm să încercați din nou mai târziu.');
        } else {
          alert(typeof message === 'string' ? message : t('parkingPayment.paymentError'));
        }
      }

    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || t('parkingPayment.paymentError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInvoiceDataSuccess = async () => {
    // Refresh driver account to get updated invoice data
    await refreshDriverAccount();
    setShowInvoiceModal(false);
    // Continue with the parking payment flow
  };

  const handleInvoiceDataClose = () => {
    setShowInvoiceModal(false);
    // Don't close the parking modal, just let user try again
  };

  if (!isOpen) return null;

  // If invoice modal is shown, don't render the main ParkingModal content
  if (showInvoiceModal) {
    return (
      <InvoiceDataModal
        isOpen={showInvoiceModal}
        onClose={handleInvoiceDataClose}
        onSuccess={handleInvoiceDataSuccess}
        driverAccount={driverAccount}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full max-w-3xl my-8 max-h-[80vh]  flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <MapPinIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white truncate">
                  {t('parkingPayment.modalTitle')}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {selectedCar ? `${selectedCar.plate} - ${selectedCar.make} ${selectedCar.model}` : 'Selectează vehiculul'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-2 py-4 overflow-y-auto flex-1">
          {selectedCar ? (
            <form onSubmit={handlePayment} className="space-y-4">
              {/* Location Information */}
              <div>
                <label className="flex items-center justify-between text-sm font-medium text-slate-700 dark:text-slate-300">
                  <span className="flex items-center gap-2">
                    {t('parkingPayment.yourLocation')}
                  </span>
                  {userLocation && (
                    <button
                      type="button"
                      onClick={getUserLocation}
                      className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                    >
                      Reîmprospătează
                    </button>
                  )}
                </label>
                {isLoadingLocation ? (
                  <div className="flex items-center space-x-2 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-700">
                    <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.detectingLocation')}</span>
                  </div>
                ) : locationCity && !useManualCity ? (
                  <div className="px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">{locationCity}</p>
                        {locationStreet && (
                          <p className="text-sm text-slate-600 dark:text-slate-400">Strada: {locationStreet}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setUseManualCity(true)}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:underline whitespace-nowrap"
                      >
                        Schimbă oraș
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {loadingCities ? (
                      <div className="flex items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                        <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                        <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">Se încarcă orașele...</span>
                      </div>
                    ) : (
                      <FormControl fullWidth variant="outlined" size="small">
                        <Select
                          value={selectedCity}
                          onChange={(e) => {
                            setSelectedCity(e.target.value);
                            setUseManualCity(true);
                          }}
                          className="bg-white dark:bg-slate-700 dark:text-white"
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgb(148 163 184)', // slate-400
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgb(168 85 247)', // purple-500
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgb(168 85 247)', // purple-500
                            },
                            '& .MuiSelect-select': {
                              color: 'inherit',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'inherit',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: 'rgb(51 65 85)', // slate-700 for dark mode
                                '& .MuiMenuItem-root': {
                                  color: 'white',
                                  backgroundColor: 'rgb(51 65 85)', // slate-700
                                  '&:hover': {
                                    backgroundColor: 'rgb(71 85 105)', // slate-600
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgb(168 85 247)', // purple-500
                                    '&:hover': {
                                      backgroundColor: 'rgb(147 51 234)', // purple-600
                                    },
                                  },
                                },
                              },
                            },
                          }}
                          required
                        >
                          {availableCities.map((city) => (
                            <MenuItem key={city.id} value={city.id}>
                              {city.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>
                )}
              </div>

              {/* Parking Zone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('parkingPayment.parkingZone')}
                </label>
                {loadingZones ? (
                  <div className="flex items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                    <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">Se încarcă zonele...</span>
                  </div>
                ) : parkingZones.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {getUniqueZones().map((z) => (
                      <button
                        key={z.zoneName}
                        type="button"
                        onClick={() => {
                          setZone(z.zoneName);
                          setSelectedDuration('');
                        }}
                        disabled={!selectedCity}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors border ${zone === z.zoneName
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {z.zoneName}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg text-center">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedCity ? 'Nu există zone disponibile pentru acest oraș' : 'Selectează mai întâi un oraș'}
                    </p>
                  </div>
                )}
              </div>

              {/* Duration */}
              {zone && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('parkingPayment.duration', 'Durată')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {getDurationsForZone(zone).map((z) => (
                      <button
                        key={z.zoneId}
                        type="button"
                        onClick={() => setSelectedDuration(z.zoneId)}
                        className={`p-3 rounded-lg font-medium transition-colors border text-center ${selectedDuration === z.zoneId
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                          }`}
                      >
                        <div className="text-sm font-semibold">
                          {z.timeInMinutes} min
                          {parseFloat(z.timeInMinutes) >= 60 &&
                            ` (${(parseFloat(z.timeInMinutes) / 60).toFixed(1)} ${parseFloat(z.timeInMinutes) === 60 ? 'oră' : 'ore'})`
                          }
                        </div>
                        <div className="text-xs opacity-75">
                          {paymentMethod === 'balance'
                            ? (typeof z.userPriceWithAccount === 'number' ? z.userPriceWithAccount.toFixed(2) : z.userPriceWithAccount)
                            : (typeof z.userPrice === 'number' ? z.userPrice.toFixed(2) : z.userPrice)
                          } {z.currency}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('parkingPayment.paymentMethod')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('balance')}
                    className={`px-4 py-2 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${paymentMethod === 'balance'
                      ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                  >
                    <WalletIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {t('parkingPayment.balance', 'Sold')}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`px-4 py-2 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${paymentMethod === 'card'
                      ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                  >
                    <CreditCardIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {t('parkingPayment.card')}
                    </p>
                  </button>
                </div>
              </div>

              {/* Balance Display */}
              {paymentMethod === 'balance' && driverAccount && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg px-4 py-2 border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <WalletIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Sold disponibil
                      </span>
                    </div>
                    <span className="text-xl font-bold text-green-600 dark:text-green-400">
                      {driverAccount.balance ? driverAccount.balance.toFixed(2) : '0.00'} RON
                    </span>
                  </div>
                  {zone && selectedDuration && selectedZoneData && calculatePrice() > (driverAccount.balance || 0) && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                      ⚠️ Sold insuficient pentru această tranzacție
                    </div>
                  )}
                </div>
              )}

              {/* Card Payment Options */}
              {paymentMethod === 'card' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('parkingPayment.selectPaymentMethod', 'Selectează metoda de plată')}
                  </label>
                  {loadingPaymentMethods ? (
                    <div className="flex items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                      <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">Se încarcă metodele de plată...</span>
                    </div>
                  ) : paymentMethods.length > 0 ? (
                    <div className="space-y-2">
                      {paymentMethods.map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => setSelectedPaymentMethodId(method.id)}
                          className={`w-full p-3 rounded-lg border-2 text-left flex items-center justify-between ${selectedPaymentMethodId === method.id
                            ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                            }`}
                        >
                          <div className="flex items-center space-x-3">
                            <CreditCardIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                            <div>
                              <p className="font-semibold text-slate-900 dark:text-white capitalize">
                                {method.brand} •••• {method.last4}
                              </p>
                              {method.expMonth && method.expYear && (
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  Expiră: {String(method.expMonth).padStart(2, '0')}/{method.expYear}
                                </p>
                              )}
                            </div>
                          </div>
                          {selectedPaymentMethodId === method.id && (
                            <CheckCircleIcon className="h-6 w-6 text-purple-600" />
                          )}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPaymentMethodId(null);
                        }}
                        className={`w-full p-3 rounded-lg border-2 border-dashed text-left flex items-center justify-center space-x-2 ${selectedPaymentMethodId === null
                          ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                          : 'border-slate-300 dark:border-slate-600 hover:border-purple-400'
                          }`}
                      >
                        <PlusCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                          {t('parkingPayment.addNewCard', 'Adaugă card nou')}
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="px-4 py-1 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center">
                        <CreditCardIcon className="h-8 w-8 mx-auto text-slate-400" />
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {t('parkingPayment.noSavedCards', 'Nu ai carduri salvate')}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 px-2">
                        <input
                          type="checkbox"
                          id="saveForFutureUse"
                          checked={saveForFutureUse}
                          onChange={(e) => setSaveForFutureUse(e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor="saveForFutureUse" className="text-sm text-slate-700 dark:text-slate-300">
                          {t('parkingPayment.saveCardForFuture', 'Salvează cardul pentru plățile viitoare')}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Price Summary */}
              {zone && selectedDuration && selectedZoneData && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                    <span>{t('parkingPayment.zone')}</span>
                    <span className="font-semibold">{selectedZoneData.zoneName}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                    <span>{t('parkingPayment.duration')}</span>
                    <span className="font-semibold">
                      {selectedZoneData.timeInMinutes} {t('parkingPayment.minutes')}
                      {parseFloat(selectedZoneData.timeInMinutes) >= 60 &&
                        ` (${(parseFloat(selectedZoneData.timeInMinutes) / 60).toFixed(1)} ${parseFloat(selectedZoneData.timeInMinutes) === 60 ? 'oră' : 'ore'})`
                      }
                    </span>
                  </div>

                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-1">
                    <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                      <span>{t('parkingPayment.parkingCost')}</span>
                      <span>
                        {paymentMethod === 'balance'
                          ? (parseFloat(String(selectedZoneData.userPriceWithAccount).replace(',', '.')) || 0).toFixed(2)
                          : (parseFloat(String(selectedZoneData.userPriceWithAccount).replace(',', '.')) || 0).toFixed(2)
                        } {selectedZoneData.currency}
                      </span>
                    </div>
                    {paymentMethod === 'card' && calculateProcessingFee() > 0 && (
                      <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-500">
                        <span>{t('parkingPayment.processingFee')}</span>
                        <span>{calculateProcessingFee().toFixed(2)} {selectedZoneData.currency}</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t-2 border-slate-300 dark:border-slate-600">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700 dark:text-slate-300 font-medium">
                        {t('parkingPayment.totalPrice')}
                      </span>
                      <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {calculatePrice().toFixed(2)} {selectedZoneData.currency}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isProcessing || !zone || !selectedCity || !selectedDuration}
                className={`w-full py-3 rounded-lg font-semibold text-white ${isProcessing || !zone || !selectedCity || !selectedDuration
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 shadow-lg'
                  }`}
              >
                {isProcessing ? t('parkingPayment.processing') : t('parkingPayment.payNow')}
              </button>
            </form>
          ) : (
            <div className="text-center py-12">
              <MapPinIcon className="h-12 w-12 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {t('quickActions.selectCarFirst')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-10 w-10 text-emerald-600 dark:text-green-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t('parkingPayment.paymentSuccess')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {t('parkingPayment.paymentSuccessMessage')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkingModal;
